import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import api from '../services/api';
import { BlogPost, BlogCategory, BlogTag, BlogPostListParams } from '../types';
import { extractApiError } from '../utils/errorUtils';
import ImageWithFallback from './ImageWithFallback';

/**
 * BlogList Component
 *
 * Public blog listing page with filtering and pagination
 * Issue #1386 - Blog platform frontend
 *
 * Features:
 * - Browse published blog posts
 * - Filter by category and tag
 * - Search posts
 * - Pagination
 * - SEO-optimized meta tags
 */

const BlogList: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [tags, setTags] = useState<BlogTag[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedTag, setSelectedTag] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch categories and tags on mount
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [categoriesData, tagsData] = await Promise.all([
          api.getBlogCategories(),
          api.getBlogTags()
        ]);
        setCategories(categoriesData);
        setTags(tagsData);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to load blog filters:', apiError);
        toast.error('Failed to load blog filters');
      }
    };
    fetchFilters();
  }, []);

  // Fetch posts when filters change
  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const params: Partial<BlogPostListParams> = {
          page,
          page_size: pageSize,
          published_only: true,
          sort_by: 'published_at',
          sort_order: 'desc'
        };

        if (selectedCategory) params.category_id = selectedCategory;
        if (selectedTag) params.tag_id = selectedTag;
        if (searchQuery.trim()) params.search = searchQuery.trim();

        const response = await api.getBlogPosts(params);
        setPosts(response.posts);
        setTotal(response.total);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to load blog posts:', apiError);
        toast.error('Failed to load blog posts');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [page, pageSize, selectedCategory, selectedTag, searchQuery]);

  const totalPages = Math.ceil(total / pageSize);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const truncateExcerpt = (text: string, maxLength: number = 200) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  return (
    <>
      <Helmet>
        <title>Blog - Local Artist Fairs</title>
        <meta name="description" content="Read the latest news, tips, and insights for artists, vendors, and fair managers from Local Artist Fairs." />
        <meta property="og:title" content="Blog - Local Artist Fairs" />
        <meta property="og:description" content="Read the latest news, tips, and insights for artists, vendors, and fair managers." />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Blog</h1>
            <p className="text-lg text-gray-600">News, tips, and insights for artists, vendors, and fair managers</p>
          </div>

          {/* Search and Filters */}
          <div className="mb-8 bg-white rounded-lg shadow-sm p-6">
            {/* Search */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search posts..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="blog-search-input"
                aria-label="Search blog posts"
              />
            </div>

            {/* Categories */}
            {categories.length > 0 && (
              <div className="mb-4">
                <h2 className="text-sm font-semibold text-gray-700 mb-2">Categories</h2>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setSelectedCategory(null);
                      setPage(1);
                    }}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === null
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid="blog-category-all"
                    aria-label="Show all categories"
                  >
                    All
                  </button>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => {
                        setSelectedCategory(category.id);
                        setPage(1);
                      }}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                        selectedCategory === category.id
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      data-testid={`blog-category-${category.slug}`}
                      aria-label={`Filter by ${category.name}`}
                    >
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Tags */}
            {tags.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-gray-700 mb-2">Tags</h2>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setSelectedTag(null);
                      setPage(1);
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      selectedTag === null
                        ? 'bg-teal-100 text-primary-dark'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid="blog-tag-all"
                    aria-label="Show all tags"
                  >
                    All
                  </button>
                  {tags.map((tag) => (
                    <button
                      key={tag.id}
                      onClick={() => {
                        setSelectedTag(tag.id);
                        setPage(1);
                      }}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        selectedTag === tag.id
                          ? 'bg-teal-100 text-primary-dark'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid={`blog-tag-${tag.slug}`}
                      aria-label={`Filter by ${tag.name} tag`}
                    >
                      {tag.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse">
                  {/* Skeleton Featured Image */}
                  <div className="aspect-video bg-gray-200"></div>

                  {/* Skeleton Content */}
                  <div className="p-6">
                    {/* Skeleton Title */}
                    <div className="h-6 bg-gray-200 rounded mb-3"></div>
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>

                    {/* Skeleton Excerpt */}
                    <div className="space-y-2 mb-4">
                      <div className="h-4 bg-gray-200 rounded"></div>
                      <div className="h-4 bg-gray-200 rounded"></div>
                      <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                    </div>

                    {/* Skeleton Date & Button */}
                    <div className="flex items-center justify-between mt-4">
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                      <div className="h-10 bg-gray-200 rounded w-24"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Posts Grid */}
          {!loading && posts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {posts.map((post) => (
                <article key={post.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  {/* Featured Image - Issue #1404 with inline SVG placeholder fallback */}
                  {post.featured_image_url && (
                    <ImageWithFallback
                      src={post.featured_image_url}
                      alt={post.title}
                      loading="lazy"
                    />
                  )}

                  {/* Content */}
                  <div className="p-6">
                    {/* Categories */}
                    {post.categories && post.categories.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {post.categories.map((category) => (
                          <span key={category.id} className="text-xs font-medium text-secondary bg-teal-50 px-2 py-1 rounded">
                            {category.name}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Title */}
                    <h2 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                      <Link to={`/blog/${post.slug}`} className="hover:text-secondary transition-colors">
                        {post.title}
                      </Link>
                    </h2>

                    {/* Excerpt */}
                    {post.excerpt && (
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {truncateExcerpt(post.excerpt)}
                      </p>
                    )}

                    {/* Meta */}
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{post.published_at ? formatDate(post.published_at) : 'Draft'}</span>
                      {post.view_count !== undefined && post.view_count > 0 && (
                        <span>{post.view_count} views</span>
                      )}
                    </div>

                    {/* Tags */}
                    {post.tags && post.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {post.tags.slice(0, 3).map((tag) => (
                          <span key={tag.id} className="text-xs text-gray-500">
                            #{tag.name}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Read More */}
                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-block mt-4 text-secondary font-medium hover:text-primary-dark transition-colors"
                    >
                      Read more →
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && posts.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">No posts found</h3>
              <p className="mt-1 text-gray-500">
                {searchQuery || selectedCategory || selectedTag
                  ? 'Try adjusting your filters or search query.'
                  : 'Check back soon for new content!'}
              </p>
            </div>
          )}

          {/* Pagination */}
          {!loading && totalPages > 1 && (
            <nav className="flex items-center justify-center gap-2" aria-label="Blog pagination">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={page === 1 ? "Previous page (currently on first page)" : "Go to previous page"}
                data-testid="blog-pagination-previous"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-gray-700" aria-current="page">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label={page === totalPages ? "Next page (currently on last page)" : "Go to next page"}
                data-testid="blog-pagination-next"
              >
                Next
              </button>
            </nav>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogList;
